import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Columns3, GripVertical, Lock } from "lucide-react";
import { cn } from "@/lib/utils";

// Show/hide + drag-reorder columns. Locked columns can't be hidden or moved
// past the display safety rules — they always remain visible.
export function ColumnManager({ pageDef, state, update }) {
  const byKey = Object.fromEntries(pageDef.columns.map((c) => [c.key, c]));
  const order = (state.columns?.order || pageDef.columns.map((c) => c.key)).filter((k) => byKey[k]);
  pageDef.columns.forEach((c) => { if (!order.includes(c.key)) order.push(c.key); });
  const hidden = new Set(state.columns?.hidden || []);
  const [dragKey, setDragKey] = useState(null);

  const setColumns = (next) => update((s) => ({ ...s, columns: { ...s.columns, ...next } }));

  const toggle = (key) => {
    const col = byKey[key];
    if (col.locked) return;
    const h = new Set(hidden);
    h.has(key) ? h.delete(key) : h.add(key);
    setColumns({ hidden: [...h] });
  };

  const onDrop = (targetKey) => {
    if (!dragKey || dragKey === targetKey) return;
    const next = [...order];
    const from = next.indexOf(dragKey);
    next.splice(from, 1);
    const to = next.indexOf(targetKey);
    next.splice(to, 0, dragKey);
    setColumns({ order: next });
    setDragKey(null);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="column-manager-trigger">
          <Columns3 className="h-4 w-4" /> Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2" data-testid="column-manager">
        <div className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Columns</div>
        <div className="space-y-0.5">
          {order.map((key) => {
            const col = byKey[key];
            return (
              <div key={key} draggable={!col.locked}
                onDragStart={() => setDragKey(key)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(key)}
                className={cn("flex items-center gap-2 rounded-md px-2 py-1.5", dragKey === key && "bg-muted")}
                data-testid={`column-item-${key}`}>
                <GripVertical className={cn("h-3.5 w-3.5", col.locked ? "text-transparent" : "cursor-grab text-muted-foreground")} />
                <span className="flex-1 truncate text-sm text-foreground">{col.label}</span>
                {col.locked ? (
                  <Lock className="h-3.5 w-3.5 text-muted-foreground" title="Required column" />
                ) : (
                  <Switch checked={!hidden.has(key)} onCheckedChange={() => toggle(key)} data-testid={`column-toggle-${key}`} />
                )}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
